import 'moment-duration-format';
import React, { useCallback, useEffect } from 'react';
import { Provider } from 'react-redux';
import { Toaster } from 'sonner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WagmiProvider } from 'wagmi';
import { ConnectKitProvider } from 'connectkit';

import App, { AppProps, AppContext } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { appWithTranslation, useTranslation } from 'next-i18next';

import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import theme from '~/styles/theme';
import { useStore } from '~/store/with-redux';
import { InitialState } from '~/store/configure-store';
import NProgress from 'nprogress';

// Disable spinner, keep only progress bar
NProgress.configure({ showSpinner: false });
import Router from 'next/router';
import nextI18NextConfig from '../../next-i18next.config';
import { CacheProvider, EmotionCache } from '@emotion/react';
import createEmotionCache from '~/styles/emotionCache';
import TourGuideProvider from '~/components/tour-guide';
import { TssCacheProvider } from 'tss-react';
import { IpfsGatewaysProvider } from '@gu-corp/react-ipfs-media';
import { getEnv } from '~/env';
import { wagmiConfig } from '~/wagmi.config';

const queryClient = new QueryClient();

const IPFS_GATEWAY_URLS = [
  getEnv('NEXT_PUBLIC_IPFS_GATEWAY_URL') || 'https://joc-dashboard.infura-ipfs.io/ipfs/',
];

interface CustomPageProps {
  initialReduxState?: string;
  title?: string;
  [key: string]: unknown;
}

const MyApp = ({
  Component,
  pageProps,
  emotionCache = createEmotionCache(),
}: AppProps<CustomPageProps> & { emotionCache: EmotionCache }) => {
  const store = useStore(
    pageProps.initialReduxState ? JSON.parse(pageProps.initialReduxState) : InitialState,
  );
  const { t, i18n } = useTranslation();
  const router = useRouter();

  const onRouteStart = useCallback(() => NProgress.start(), []);
  const onRouteDone = useCallback(() => NProgress.done(), []);

  // Check localStorage for saved language preference and redirect if needed
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    const supportedLocales = router.locales || ['en', 'ja'];
    if (
      savedLanguage &&
      savedLanguage !== router.locale &&
      supportedLocales.includes(savedLanguage)
    ) {
      // User has a saved preference that differs from current locale
      router.replace(router.asPath, router.asPath, { locale: savedLanguage });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    Router.events.on('routeChangeStart', onRouteStart);
    Router.events.on('routeChangeComplete', onRouteDone);
    Router.events.on('routeChangeError', onRouteDone);

    return () => {
      Router.events.off('routeChangeStart', onRouteStart);
      Router.events.off('routeChangeComplete', onRouteDone);
      Router.events.off('routeChangeError', onRouteDone);
    };
  }, [onRouteStart, onRouteDone]);

  return (
    <React.Fragment>
      <Head>
        <title>{pageProps.title ? `${t(pageProps.title)} - JOC Club` : 'JOC Club'}</title>
        <meta
          name="viewport"
          content="width=device-width, minimum-scale=1, initial-scale=1, maximum-scale=1"
        />
        <meta name="description" content="JOC Club" />
        <link rel="preconnect" href="https://fonts.gstatic.com" />
      </Head>
      <CacheProvider value={emotionCache}>
        <TssCacheProvider value={emotionCache}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <IpfsGatewaysProvider gateways={IPFS_GATEWAY_URLS}>
              <Provider store={store}>
                <WagmiProvider config={wagmiConfig}>
                  <QueryClientProvider client={queryClient}>
                    <ConnectKitProvider
                      options={{
                        language: i18n.language === 'ja' ? 'ja-JP' : 'en-US',
                        hideNoWalletCTA: true,
                      }}
                    >
                      <TourGuideProvider>
                        <Component {...pageProps} />
                        <Toaster position="bottom-right" richColors />
                      </TourGuideProvider>
                    </ConnectKitProvider>
                  </QueryClientProvider>
                </WagmiProvider>
              </Provider>
            </IpfsGatewaysProvider>
          </ThemeProvider>
        </TssCacheProvider>
      </CacheProvider>
    </React.Fragment>
  );
};

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);

  return {
    ...appProps,
  };
};

export default appWithTranslation(MyApp as any, nextI18NextConfig);
